/**
 * @voltxampere/web — Onboarding tour step config (Sprint 23 C2)
 *
 * 6-step plan per Şefim's list. Each step is either:
 *   - "modal" (steps 0, 5): viewport-centered card, full-screen
 *     backdrop, no spotlight target
 *   - "coachmark" (steps 1-4): spotlight overlay with hole over
 *     `targetSelector`, tooltip flow-positioned next to it
 *
 * If a coachmark target selector cannot be found in the DOM at
 * render time, the step gracefully degrades to a modal (centered
 * card, no spotlight) so the tour does not get stuck.
 *
 * `titleKey` and `bodyKey` are typed i18n bundle keys; the
 * component renders them via `t()`.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { TranslationKey } from "../i18n/index.js";

export type TourStepKind = "modal" | "coachmark";

export interface TourStep {
  readonly kind: TourStepKind;
  readonly titleKey: TranslationKey;
  readonly bodyKey: TranslationKey;
  /**
   * CSS selector(s) — `coachmark` step queries the document with
   * `document.querySelector(selectors[i])` and uses the first
   * match. Modal steps leave it undefined.
   */
  readonly targetSelectors?: readonly string[];
}

/**
 * Sprint 23 C2 canonical 6 steps (Şefim's list).
 *
 *  0. Welcome modal — "Hoş geldin"
 *  1. Sidebar coachmark — "Yan panelden bileşen seç"
 *  2. Canvas coachmark — "Canvas'a yerleştir"
 *  3. F5 sim modal — "F5 ile simüle et" (keyboard hint, modal)
 *  4. F6 AC modal — "F6 ile AC analiz" (keyboard hint, modal)
 *  5. Cmd+K palette modal — "Cmd+K ile her şey"
 *
 * Steps 1-2 try to spotlight DOM elements; the rest are
 * keyboard/visual hints rendered as modals.
 */
export const TOUR_STEPS: readonly TourStep[] = [
  {
    kind: "modal",
    titleKey: "tour.welcome.title",
    bodyKey: "tour.welcome.body",
  },
  {
    kind: "coachmark",
    titleKey: "tour.sidebar.title",
    bodyKey: "tour.sidebar.body",
    targetSelectors: [
      "vxa-sidebar",
      ".vxa-sidebar",
      ".symbol-library",
      'aside[data-pane="sidebar"]',
    ],
  },
  {
    kind: "coachmark",
    titleKey: "tour.canvas.title",
    bodyKey: "tour.canvas.body",
    targetSelectors: ["vxa-canvas", ".vxa-canvas", "canvas[data-role=schematic]"],
  },
  {
    kind: "modal",
    titleKey: "tour.simulate.title",
    bodyKey: "tour.simulate.body",
  },
  {
    kind: "modal",
    titleKey: "tour.ac.title",
    bodyKey: "tour.ac.body",
  },
  {
    kind: "modal",
    titleKey: "tour.palette.title",
    bodyKey: "tour.palette.body",
  },
];
